import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CloseIcon from "@mui/icons-material/Close";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import InboxIcon from "@mui/icons-material/Inbox";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import TimeSlot from "src/app/models/screens/availabilityTimeslot.model";
import BookingRequest, {
  RequestManagementUser,
} from "src/app/models/screens/requestManagement.model";
import AvailabilityRequestManagementScreenViewModel from "src/app/screens/requestManagement/requestManagement.viewmodel";
import styles from "src/app/styles/screens/requestManagement.style";

const RequestManagementScreenView: React.FC = () => {
  const {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    selectedTab,
    onTabChanged,
    userSearchText,
    setUserSearchText,
    isSearchingUsers,
    searchedUsers,
    selectedUser,
    selectedUserFullName,
    onUserSelected,
    selectedUserDate,
    onSelectedUserDateChanged,
    selectedUserFreeTimeslots,
    isLoadingSelectedUserTimeslots,
    selectedBookableSlot,
    onBookableSlotSelected,
    requestDescription,
    setRequestDescription,
    onSubmitBookingRequest,
    receivedRequests,
    isLoadingReceivedRequests,
    onAcceptRequest,
    onRejectRequest,
    minutesToLabel,
    getStatusChipColor,
    closeEmergencyRequestModal,
    isEmergencyRequestModalOpen,
    openEmergencyRequestModal,
    isEmergencyRequestButtonLoading,
    onComposeEmergencyRequestConfirmed,
    emergencyRequestDescription,
    setEmergencyRequestDescription,
    userRole,
    children,
    isLoadingChildren,
    selectedRequestOwnerId,
    onSelectedRequestOwnerChanged,
    adminRequestUsers,
    isLoadingAdminRequestUsers,
    isViewingAnotherUsersRequestsAsAdmin,
  } = AvailabilityRequestManagementScreenViewModel();

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Requests" />
      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              Request Management
            </Typography>
          </Grid>
          {userRole === "Parent" && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="request-owner-select-label">
                      Request Owner
                    </InputLabel>
                    <Select
                      labelId="request-owner-select-label"
                      value={selectedRequestOwnerId}
                      label="Request Owner"
                      onChange={(event: SelectChangeEvent) =>
                        onSelectedRequestOwnerChanged(event.target.value)
                      }
                      disabled={isLoadingChildren}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {children.map((child) => (
                        <MenuItem key={child._id} value={child._id}>
                          {child.firstName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}

          {userRole === "Admin" && selectedTab === 1 && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="admin-request-owner-select-label">
                      Received Requests Of
                    </InputLabel>
                    <Select
                      labelId="admin-request-owner-select-label"
                      value={selectedRequestOwnerId}
                      label="Received Requests Of"
                      onChange={(event: SelectChangeEvent) =>
                        onSelectedRequestOwnerChanged(event.target.value)
                      }
                      disabled={isLoadingAdminRequestUsers}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {adminRequestUsers.map((user: RequestManagementUser) => (
                        <MenuItem key={user.id} value={user.id}>
                          {`${user.firstName} ${user.lastName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Paper elevation={0} sx={styles.tabsContainer}>
              {userRole && userRole !== "Guest" && (
                <Tabs
                  value={selectedTab}
                  onChange={onTabChanged}
                  variant="fullWidth"
                  scrollButtons="auto"
                >
                  <Tab
                    icon={<PersonSearchIcon />}
                    iconPosition="start"
                    label="Look Up Users"
                  />
                  <Tab
                    icon={<InboxIcon />}
                    iconPosition="start"
                    label="Received Requests"
                  />
                </Tabs>
              )}
              <Divider />
              {selectedTab === 0 && (
                <Box sx={styles.selectedTab}>
                  <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Paper elevation={0} sx={styles.selectedTabContent}>
                        <Typography sx={styles.selectedTabText}>
                          Users
                        </Typography>
                        <TextField
                          fullWidth
                          label="Search users"
                          value={userSearchText}
                          onChange={(e) => setUserSearchText(e.target.value)}
                          sx={styles.searchBox}
                        />
                        {isSearchingUsers ? (
                          <Box sx={styles.searchingLoader}>
                            <CircularProgress size={28} />
                          </Box>
                        ) : (
                          <List sx={styles.usersListParent}>
                            {searchedUsers.map(
                              (user: RequestManagementUser) => {
                                const isSelected: boolean =
                                  selectedUser?.id === user.id;
                                return (
                                  <ListItemButton
                                    key={user.id}
                                    onClick={() => onUserSelected(user)}
                                    sx={[
                                      styles.usersListItem,
                                      {
                                        backgroundColor: isSelected
                                          ? "rgba(25,118,210,0.08)"
                                          : "transparent",
                                      },
                                    ]}
                                  >
                                    <ListItemText
                                      primary={`${user.firstName} ${user.lastName}`}
                                    />
                                  </ListItemButton>
                                );
                              },
                            )}
                            {searchedUsers.length === 0 && (
                              <Typography sx={styles.noUsersText}>
                                No users found.
                              </Typography>
                            )}
                          </List>
                        )}
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Paper elevation={0} sx={styles.calendarContainer}>
                        <Typography sx={styles.calendarTitleText}>
                          {selectedUser
                            ? `${selectedUserFullName} Calendar`
                            : "Select a user"}
                        </Typography>
                        {selectedUser ? (
                          <>
                            <DateCalendar
                              value={selectedUserDate}
                              onChange={onSelectedUserDateChanged}
                            />
                            <Typography>Or</Typography>
                            <Button
                              variant="contained"
                              sx={styles.emergencyMeetingButton}
                              onClick={openEmergencyRequestModal}
                            >
                              Request Emergency Meeting
                            </Button>
                          </>
                        ) : (
                          <Typography sx={styles.subTitleText}>
                            Choose a user to see their availability.
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Paper elevation={0} sx={styles.selectedTabContent}>
                        <Typography sx={styles.calendarTitleText}>
                          Free Timeslots
                        </Typography>

                        {!selectedUserDate ? (
                          <Typography sx={styles.subTitleText}>
                            Select a date to see free timeslots.
                          </Typography>
                        ) : isLoadingSelectedUserTimeslots ? (
                          <Box sx={styles.searchingLoader}>
                            <CircularProgress size={28} />
                          </Box>
                        ) : (
                          <>
                            <Box sx={styles.timeslotsContainer}>
                              {selectedUserFreeTimeslots.length === 0 && (
                                <Typography sx={styles.subTitleText}>
                                  No free timeslots for this date.
                                </Typography>
                              )}
                              {selectedUserFreeTimeslots.map(
                                (slot: TimeSlot) => {
                                  const isSelected: boolean =
                                    selectedBookableSlot?.from === slot.from &&
                                    selectedBookableSlot?.to === slot.to;
                                  return (
                                    <Chip
                                      key={`${slot.from}-${slot.to}`}
                                      label={`${minutesToLabel(
                                        slot.from,
                                      )} - ${minutesToLabel(slot.to)}`}
                                      clickable
                                      color={isSelected ? "primary" : "default"}
                                      onClick={() =>
                                        onBookableSlotSelected(slot)
                                      }
                                    />
                                  );
                                },
                              )}
                            </Box>
                            <TextField
                              fullWidth
                              multiline
                              minRows={4}
                              label="Description"
                              value={requestDescription}
                              onChange={(e) =>
                                setRequestDescription(e.target.value)
                              }
                              sx={styles.searchBox}
                            />
                            <Button
                              fullWidth
                              variant="contained"
                              startIcon={<EventAvailableIcon />}
                              disabled={
                                !selectedBookableSlot ||
                                !requestDescription.trim()
                              }
                              onClick={onSubmitBookingRequest}
                            >
                              Book Selected Timeslot
                            </Button>
                          </>
                        )}
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
              {selectedTab === 1 && (
                <Box sx={styles.selectedTab}>
                  <Typography sx={styles.selectedTabText}>
                    Requests Received
                  </Typography>
                  {isLoadingReceivedRequests ? (
                    <Box sx={styles.searchingLoader}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <Grid container spacing={2}>
                      {receivedRequests.map((request: BookingRequest) => (
                        <Grid key={request.id} size={{ xs: 12, md: 6 }}>
                          <Paper
                            elevation={0}
                            sx={[
                              styles.selectedTabContent,
                              request.emergency && request.status === "Pending"
                                ? styles.emergencyTabContent
                                : {},
                            ]}
                          >
                            <Box sx={styles.requesterNameContainer}>
                              <Typography sx={styles.requesterNameText}>
                                {request.requesterFullName}
                              </Typography>
                              <Chip
                                size="small"
                                label={request.status}
                                color={getStatusChipColor(request.status)}
                              />
                            </Box>
                            <Typography sx={styles.requesterDate}>
                              Date: {request.date}
                            </Typography>
                            <Typography sx={styles.requesterDate}>
                              {request.emergency
                                ? `Time: ${minutesToLabel(request.from)}`
                                : `Time: ${minutesToLabel(
                                    request.from,
                                  )} - ${minutesToLabel(request.to)}`}
                            </Typography>
                            <Typography sx={styles.requesterDescription}>
                              Description: {request.description}
                            </Typography>
                            {request.status === "Pending" && (
                              <Box sx={styles.requestActionsContainer}>
                                <Button
                                  variant="contained"
                                  color="success"
                                  startIcon={<CheckCircleOutlineIcon />}
                                  onClick={() => onAcceptRequest(request.id)}
                                  disabled={
                                    isViewingAnotherUsersRequestsAsAdmin
                                  }
                                >
                                  Accept
                                </Button>
                                <Button
                                  variant="outlined"
                                  color="error"
                                  startIcon={<CloseIcon />}
                                  onClick={() => onRejectRequest(request.id)}
                                  disabled={
                                    isViewingAnotherUsersRequestsAsAdmin
                                  }
                                >
                                  Reject
                                </Button>
                              </Box>
                            )}
                          </Paper>
                        </Grid>
                      ))}
                      {receivedRequests.length === 0 && (
                        <Grid size={{ xs: 12 }}>
                          <Typography sx={styles.noUsersText}>
                            No requests received.
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  )}
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>
      <Dialog
        open={isEmergencyRequestModalOpen}
        onClose={closeEmergencyRequestModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Compose Emergency Request</DialogTitle>
        <DialogContent>
          <Typography>
            Write down about the emergency request you're about to make
          </Typography>
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Description"
            value={emergencyRequestDescription}
            onChange={(e) => setEmergencyRequestDescription(e.target.value)}
            sx={styles.emergencyRequestDescriptionTextField}
          />
        </DialogContent>
        <DialogActions>
          <Box sx={styles.emergencyRequestModalActionsContainer}>
            <Button
              onClick={closeEmergencyRequestModal}
              color="inherit"
              sx={styles.modalCancelButton}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={onComposeEmergencyRequestConfirmed}
              variant="contained"
              color="primary"
              loading={isEmergencyRequestButtonLoading}
            >
              Confirm
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <SnackBarComponentView
        isError={isSnackbarError}
        isOpen={isDisplayingSnackbar}
        message={snackbarMessage}
        onClose={() => setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default RequestManagementScreenView;
